import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';
import { HttpError } from '@/lib/https';
import { cn } from '@/lib/utils';
import { useCreateSuppression } from '../../hooks/use-suppressions';
import { getSuppressionErrorMessage } from '../../services/suppressions.service';
import type { SuppressionChannelApi, SuppressionEntryApi } from '../../types/suppressions-api.types';
import {
  getSuppressionConflictMessage,
  getSuppressionHttpStatus,
  parseSuppressionFieldErrors,
} from '../../utils/suppression-api-errors';

const HELP_TEXT = 'This address will stop receiving sends on the selected channel immediately.';

const CHANNEL_OPTIONS: { value: SuppressionChannelApi; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
];

const ADDRESS_COPY: Record<
  SuppressionChannelApi,
  { label: string; placeholder: string; empty: string; invalid: string }
> = {
  email: {
    label: 'Email address',
    placeholder: 'jane@example.com',
    empty: 'Enter an email address.',
    invalid: 'Enter a valid email address.',
  },
  sms: {
    label: 'Phone number',
    placeholder: '+41 79 555 0142',
    empty: 'Enter a phone number.',
    invalid: 'Enter a valid phone number including the country code, e.g. +41 79 555 0142.',
  },
};

const validateAddress = (value: string, channel: SuppressionChannelApi): string | null => {
  if (!value) return ADDRESS_COPY[channel].empty;
  const valid =
    channel === 'email' ? z.string().email().safeParse(value).success : isValidPhoneNumber(value);
  return valid ? null : ADDRESS_COPY[channel].invalid;
};

const newIdempotencyKey = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `suppression-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export type AddSuppressionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (entry: SuppressionEntryApi) => void;
};

export const AddSuppressionDialog = ({
  open,
  onOpenChange,
  onCreated,
}: AddSuppressionDialogProps) => {
  const { mutateAsync: createSuppression, isPending } = useCreateSuppression();
  const [address, setAddress] = useState('');
  const [channel, setChannel] = useState<SuppressionChannelApi>('email');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const idempotencyKeyRef = useRef(newIdempotencyKey());
  const lastSubmittedAddressRef = useRef<string | null>(null);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setAddress('');
    setChannel('email');
    setFieldError(null);
    setFormError(null);
    idempotencyKeyRef.current = newIdempotencyKey();
    lastSubmittedAddressRef.current = null;
    submitInFlightRef.current = false;
  }, [open]);

  const handleAddressChange = (value: string) => {
    setAddress(value);
    setFieldError(null);
    setFormError(null);
    const lastSubmitted = lastSubmittedAddressRef.current;
    if (lastSubmitted !== null && value.trim() !== lastSubmitted) {
      idempotencyKeyRef.current = newIdempotencyKey();
      lastSubmittedAddressRef.current = null;
    }
  };

  const handleSubmit = async () => {
    if (isPending || submitInFlightRef.current) return;

    const trimmed = address.trim();
    const validationError = validateAddress(trimmed, channel);
    if (validationError) {
      setFieldError(validationError);
      return;
    }

    setFieldError(null);
    setFormError(null);
    lastSubmittedAddressRef.current = trimmed;
    submitInFlightRef.current = true;

    try {
      const entry = await createSuppression({
        address: trimmed,
        channel,
        idempotencyKey: idempotencyKeyRef.current,
      });
      onCreated(entry);
      onOpenChange(false);
    } catch (error) {
      const status = getSuppressionHttpStatus(error);

      if (status === 409) {
        setFieldError(
          getSuppressionConflictMessage(error) ?? getSuppressionErrorMessage(error)
        );
        return;
      }

      if (status === 400 && error instanceof HttpError) {
        const fields = parseSuppressionFieldErrors(
          error.error as { errors?: string[] | null }
        );
        const addressMessage = fields.Address?.[0];
        const channelMessage = fields.Channel?.[0];
        const genericMessage = fields._generic?.[0];

        if (addressMessage || channelMessage) {
          setFieldError(addressMessage ?? channelMessage ?? null);
          return;
        }

        setFormError(genericMessage ?? getSuppressionErrorMessage(error));
        return;
      }

      setFormError(getSuppressionErrorMessage(error));
    } finally {
      submitInFlightRef.current = false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px] gap-0 overflow-hidden p-0 sm:rounded-lg">
        <DialogHeader className="space-y-0 border-b border-neutral-200 px-5 py-4">
          <DialogTitle className="text-lg font-semibold text-neutral-900">
            Add to suppression
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 px-5 py-5">
          <div className="space-y-1.5">
            <Label>Channel</Label>
            <RadioGroup
              value={channel}
              onValueChange={(value) => {
                setChannel(value as SuppressionChannelApi);
                setFieldError(null);
                setFormError(null);
                idempotencyKeyRef.current = newIdempotencyKey();
                lastSubmittedAddressRef.current = null;
              }}
              className="flex flex-row gap-4"
              disabled={isPending}
            >
              {CHANNEL_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  htmlFor={`add-suppression-channel-${option.value}`}
                  className="flex cursor-pointer items-center gap-2 text-sm text-neutral-900"
                >
                  <RadioGroupItem
                    value={option.value}
                    id={`add-suppression-channel-${option.value}`}
                    className="border-neutral-400 text-primary-600"
                  />
                  {option.label}
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-suppression-address">{ADDRESS_COPY[channel].label}</Label>
            <Input
              id="add-suppression-address"
              type={channel === 'email' ? 'email' : 'tel'}
              autoComplete="off"
              placeholder={ADDRESS_COPY[channel].placeholder}
              value={address}
              disabled={isPending}
              aria-invalid={Boolean(fieldError)}
              className={cn(
                'h-10',
                fieldError && 'border-error focus-visible:ring-error/30'
              )}
              onChange={(event) => handleAddressChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleSubmit();
                }
              }}
            />
            <p
              className={cn(
                'text-[13px] leading-[18px]',
                fieldError ? 'text-error' : 'text-neutral-500'
              )}
              role={fieldError ? 'alert' : undefined}
            >
              {fieldError ?? HELP_TEXT}
            </p>
          </div>

          <p className="text-[13px] leading-[18px] text-neutral-600">
            Reason: <span className="font-medium text-neutral-900">Manual</span> — saved
            automatically for entries added here.
          </p>

          {formError ? (
            <p className="text-[13px] leading-[18px] text-error" role="alert">
              {formError}
            </p>
          ) : null}
        </div>

        <DialogFooter className="flex-row justify-end gap-2 border-t border-neutral-200 px-5 py-4 sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            className="h-10 px-4"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="h-10 bg-primary-600 px-4 text-white hover:bg-primary-700"
            disabled={isPending}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {isPending ? 'Adding…' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
